import { AsyncPipe, isPlatformBrowser, NgFor, NgIf } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	Inject,
	Input,
	OnDestroy,
	OnInit,
	Optional,
	PLATFORM_ID,
	TrackByFunction,
} from '@angular/core';
import { Router } from '@angular/router';

import {
	ACCOUNT_MY_ORDERS_PATH,
	ACCOUNT_PATH,
	ChangeOrderService,
	EDR_BOOST_OFFERS_PATH,
	EDR_LANDING_PAGE_PATH,
} from '@woolworthsnz/shop';
import {
	AppSettingsService,
	DatalayerService,
	EditOrderNotificationComponent,
	FeatureService,
	LastOrderFulfilment,
	ShopperService,
	TRADER_BASE_URL,
} from '@woolworthsnz/styleguide';
import { ContextResponse } from '@woolworthsnz/trader-api';
import { filter, Observable, Subject, switchMap } from 'rxjs';
import { ServiceBarItem, ServiceBarItemComponent } from '../service-bar-item/service-bar-item.component';
import { map, takeUntil } from 'rxjs/operators';
import { edrConfig, EverydayRewardsFacade } from '@woolworthsnz/everyday-rewards';
import EnabledFeaturesEnum = ContextResponse.EnabledFeaturesEnum;

export enum PredefinedServiceBarItems {
	EdrBoosts = 'edr-boosts',
}

@Component({
	selector: 'dc-service-bar',
	template: `
		<div class="heading--1" *ngIf="icons?.length > 0">
			Kia ora<span *ngIf="(shopperService.state$ | async)?.firstName"
				>, {{ (shopperService.state$ | async)?.firstName }}</span
			>
		</div>
		<ng-container *ngIf="browser">
			<cdx-edit-order-notification
				*ngIf="lastOrderFulfilment$ | async as lastOrderFulfilment"
				[lastOrderFulfilment]="lastOrderFulfilment"
				[editOrderUrl]="changeOrderRouteFromHomePage"
			></cdx-edit-order-notification>
		</ng-container>
		<nav>
			<ng-container *ngFor="let icon of icons; trackBy: trackByFn">
				<dc-service-bar-item
					name="{{ icon.name }}"
					text="{{ icon.text }}"
					[link]="getLink(icon)"
					image="{{ icon.image }}"
					[badgeValue]="
						icon.name === predefinedServiceBarItems.EdrBoosts && ((boostOffersCount$ | async) ?? undefined)
					"
					[badgeColor]="boostBadgeColor"
					(clickEmitter)="handleServiceBarItemClick($event)"
					[tooltip]="icon.toolTipText"
				></dc-service-bar-item>
			</ng-container>
		</nav>
	`,
	styleUrls: ['./service-bar.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, EditOrderNotificationComponent, NgFor, ServiceBarItemComponent, AsyncPipe],
})
export class ServiceBarComponent implements OnInit, OnDestroy {
	@Input() public icons: ServiceBarItem[];

	public lastOrderFulfilment$: Observable<LastOrderFulfilment | undefined>;

	public predefinedServiceBarItems = PredefinedServiceBarItems;
	public boostOffersCount$: Observable<number | undefined>;
	public boostBadgeColor = edrConfig.colors.primary.orange;
	public changeOrderRouteFromServiceBar = `${
		this.traderBaseUrl ?? ''
	}/${ACCOUNT_PATH}/${ACCOUNT_MY_ORDERS_PATH}/(account-modal:change-order)?source=servicebar`;
	public changeOrderRouteFromHomePage = `${
		this.traderBaseUrl ?? ''
	}/${ACCOUNT_PATH}/${ACCOUNT_MY_ORDERS_PATH}/(account-modal:change-order)?source=homepage`;

	private edrLandingPageUrl = `/${EDR_LANDING_PAGE_PATH}`;
	private isLoggedIn = false;
	private destroyed$: Subject<boolean> = new Subject();

	constructor(
		public shopperService: ShopperService,
		private datalayerService: DatalayerService,
		private changeOrderService: ChangeOrderService,
		private featureService: FeatureService,
		private appSettingsService: AppSettingsService,
		private edrFacade: EverydayRewardsFacade,
		private router: Router,
		@Inject(PLATFORM_ID) private platformId: Object,
		@Optional() @Inject(TRADER_BASE_URL) private traderBaseUrl: string
	) {
		this.assignLastOrderFulfilment();
	}

	get browser(): boolean {
		return isPlatformBrowser(this.platformId);
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
	}

	public ngOnInit(): void {
		this.shopperService.state$
			.pipe(map((s) => s.isLoggedIn))
			.pipe(takeUntil(this.destroyed$))
			.subscribe((isLoggedIn) => {
				this.isLoggedIn = isLoggedIn ?? false;
			});

		this.edrLandingPageUrl = `${
			this.appSettingsService.getSetting('accountBaseUrl') ?? ''
		}/${EDR_LANDING_PAGE_PATH}`;

		this.boostOffersCount$ = this.edrFacade.boostOffersCount$;
	}

	public handleServiceBarItemClick(itemName: string): void {
		if (itemName !== 'change-order') {
			this.datalayerService.trackServiceBarIconEvent(itemName);
		}
	}

	public trackByFn: TrackByFunction<ServiceBarItem> = (_: any, item: ServiceBarItem) => item.name;

	public getLink(icon: ServiceBarItem): string {
		if (icon.name === PredefinedServiceBarItems.EdrBoosts) {
			if (this.isLoggedIn) {
				return `/${EDR_BOOST_OFFERS_PATH}`;
			} else {
				return this.edrLandingPageUrl;
			}
		} else if (icon.name === 'change-order') {
			icon.link = this.changeOrderRouteFromServiceBar;
		}
		return icon.link;
	}

	private assignLastOrderFulfilment(): void {
		this.lastOrderFulfilment$ = this.featureService.isEnabled(EnabledFeaturesEnum.ChangeOrderPromptHomepage).pipe(
			filter((isFeatureForHomePageEnabled) => isFeatureForHomePageEnabled),
			switchMap(() => this.changeOrderService.getLastOrderFulfilment())
		);
	}
}
